/*
  <search-palette>: the site search overlay.

  Opens from any <drawer-button data-target="drawer-search"> (the theme's
  DrawerButton calls open()), from Cmd/Ctrl+K, or from "/" when nothing
  else has focus. Results are server-rendered by the predictive-search
  section and swapped into [data-results]; this file only owns timing,
  keyboard handling, recent searches and the open/close state.
*/
class SearchPalette extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.results = this.querySelector('[data-results]');
    this.empty = this.querySelector('[data-empty]');
    this.recent = this.querySelector('[data-recent]');
    this.recentList = this.querySelector('[data-recent-list]');
    this.status = this.querySelector('[data-status]');
    this.clearButton = this.querySelector('[data-clear]');
    this.predictiveUrl = this.dataset.predictiveUrl;
    this.searchUrl = this.dataset.searchUrl;
    this.cache = new Map();
    this.controller = null;
    this.timer = null;
    this.term = '';
    this.trigger = null;
    this.storageKey = 'wvh:recent-searches';

    this.onKeydown = this.onKeydown.bind(this);
    this.onGlobalKeydown = this.onGlobalKeydown.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
  }

  connectedCallback() {
    this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.clearButton.addEventListener('click', () => this.clear(true));
    this.input.addEventListener('input', () => this.onInput());
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('keydown', this.onGlobalKeydown);
    this.querySelectorAll('[data-term]').forEach((chip) => {
      chip.addEventListener('click', () => this.search(chip.dataset.term));
    });
    this.results.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link) this.remember(this.term);
    });
    this.renderRecent();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onGlobalKeydown);
  }

  /* ---------- open / close (same contract as the theme's drawers) ---------- */

  open(trigger) {
    this.trigger = trigger || null;
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    this.renderRecent();
    /* Keep focus inside the dialog while it is open. The drawer this
       replaced did the same (ComponentDrawer.trapFocus in theme.js);
       without it, tabbing walks into the page behind the scrim. */
    document.addEventListener('focus', this.trapFocus, true);
    /* A timer rather than an animation frame: frames are throttled in
       background tabs, and the input must take focus either way. */
    setTimeout(() => {
      this.input.focus({ preventScroll: true });
      if (this.input.value) this.input.select();
    }, 0);
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    /* Released before the trigger is refocused, or the trap bounces it back. */
    document.removeEventListener('focus', this.trapFocus, true);
    if (this.trigger && typeof this.trigger.focus === 'function') this.trigger.focus();
    this.trigger = null;
  }

  /* Everything tabbable inside the dialog, in document order. */
  focusables() {
    return Array.from(
      this.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null && !el.disabled && !el.hidden);
  }

  /* Tab wrapping is the trap that actually holds: re-focusing inside a
     focus event is unreliable, because the browser finishes the original
     focus after the handler returns. */
  handleTab(event) {
    const items = this.focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!this.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Backstop for focus moved by something other than Tab (a script, a
     click on the page behind). Deferred so it lands after the browser
     has finished the focus it is already processing. */
  trapFocus(event) {
    if (this.hasAttribute('open') && !this.contains(event.target)) {
      setTimeout(() => {
        if (this.hasAttribute('open') && !this.contains(document.activeElement)) this.input.focus();
      }, 0);
    }
  }

  /* ---------- input ---------- */

  onInput() {
    const value = this.input.value.trim();
    this.clearButton.hidden = value.length === 0;
    clearTimeout(this.timer);
    if (value.length < 2) {
      this.term = value;
      this.showEmpty();
      return;
    }
    this.timer = setTimeout(() => this.fetchResults(value), 200);
  }

  search(term) {
    this.input.value = term;
    this.input.focus();
    this.onInput();
  }

  clear(refocus) {
    this.input.value = '';
    this.term = '';
    this.clearButton.hidden = true;
    if (this.controller) this.controller.abort();
    this.showEmpty();
    if (refocus) this.input.focus();
  }

  onSubmit(event) {
    if (!this.input.value.trim()) {
      event.preventDefault();
      return;
    }
    this.remember(this.input.value.trim());
  }

  /* ---------- results ---------- */

  async fetchResults(value) {
    this.term = value;
    const key = value.toLowerCase();
    if (this.cache.has(key)) {
      this.render(this.cache.get(key));
      return;
    }
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.setAttribute('loading', '');
    this.announce(this.dataset.loadingText);
    const url = `${this.predictiveUrl}?q=${encodeURIComponent(value)}&resources%5Btype%5D=product,query,collection&resources%5Blimit%5D=10&resources%5Blimit_scope%5D=each&section_id=predictive-search`;
    try {
      const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin', mode: 'same-origin' });
      if (!response.ok) throw new Error(response.status);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');
      const markup = section ? section.innerHTML : '';
      this.cache.set(key, markup);
      if (this.term === value) this.render(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.removeAttribute('loading');
    }
  }

  render(markup) {
    this.results.innerHTML = markup;
    this.removeAttribute('loading');
    this.setAttribute('results', '');
    const count = this.results.querySelector('[data-count]');
    if (count) this.announce(count.textContent.trim());
  }

  showEmpty() {
    this.results.innerHTML = '';
    this.removeAttribute('results');
    this.removeAttribute('loading');
  }

  announce(text) {
    if (!this.status) return;
    this.status.textContent = text || '';
  }

  /* ---------- keyboard ---------- */

  onGlobalKeydown(event) {
    const isOpen = this.hasAttribute('open');
    const meta = event.metaKey || event.ctrlKey;
    if (meta && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      isOpen ? this.close() : this.open(document.activeElement);
      return;
    }
    if (isOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
      return;
    }
  }

  /* Whatever is on screen right now: results once a query has run,
     otherwise the recent searches, popular products and browse chips. */
  navTargets() {
    const scope = this.hasAttribute('results') ? this.results : this.empty;
    if (!scope) return [];
    return Array.from(scope.querySelectorAll('a[href], button')).filter((el) => el.offsetParent !== null);
  }

  onKeydown(event) {
    if (event.key === 'Tab') {
      this.handleTab(event);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = this.navTargets();
    if (!items.length) return;
    event.preventDefault();
    /* Real DOM focus rather than aria-activedescendant: the row is
       announced without a listbox contract to get wrong, and the focus
       ring lands where the eye already is. */
    const current = items.indexOf(document.activeElement);
    let next;
    if (event.key === 'ArrowDown') next = current === -1 || current === items.length - 1 ? 0 : current + 1;
    else if (current === -1) next = items.length - 1;
    else next = current === 0 ? items.length - 1 : current - 1;
    items[next].focus();
    items[next].scrollIntoView({ block: 'nearest' });
  }

  /* ---------- recent searches (per browser, never leaves the device) ---------- */

  readRecent() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      const list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];
      return list
        .filter((s) => typeof s === 'string')
        .map((s) => s.replace(/[\u0000-\u001f\u007f<>]/g, '').trim().slice(0, 60))
        .filter((s) => s.length >= 2)
        .slice(0, 5);
    } catch (e) {
      return [];
    }
  }

  remember(term) {
    const value = (term || '').replace(/[\u0000-\u001f\u007f<>]/g, '').trim().slice(0, 60);
    if (value.length < 2) return;
    try {
      const list = [value, ...this.readRecent().filter((s) => s.toLowerCase() !== value.toLowerCase())].slice(0, 5);
      localStorage.setItem(this.storageKey, JSON.stringify(list));
    } catch (e) {
      /* storage unavailable: recents are a convenience, not a feature */
    }
  }

  renderRecent() {
    if (!this.recent || !this.recentList) return;
    const list = this.readRecent();
    this.recent.hidden = list.length === 0;
    this.recentList.innerHTML = '';
    list.forEach((term) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'search-palette__query';
      button.textContent = term;
      button.addEventListener('click', () => this.search(term));
      li.appendChild(button);
      this.recentList.appendChild(li);
    });
  }
}

customElements.define('search-palette', SearchPalette);
